import { Item } from '@/features/backlog/types';
import { epicColor } from '@/features/backlog/constants';
import { TaskTypeGlyph } from '@/components/ui/icons';
import { cn } from '@/components/ui/cn';

/**
 * A small coloured chip naming the epic an item is linked to (JIRA-style) — meant to sit above the
 * key + title on a card/row. Renders nothing when the item has no epic. Tinted with the epic type
 * colour so it reads as "belongs to an epic" at a glance in both themes. If `onClick` is given the
 * chip becomes a button (e.g. to focus/open that epic).
 */
export default function EpicChip({ epic, onClick, className }: { epic?: Item; onClick?: () => void; className?: string }) {
  if (!epic) return null;
  const color = epicColor(epic);
  const label = epic.title || epic.key;
  const cls = cn(
    'inline-flex max-w-full items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-semibold leading-none',
    className,
  );
  const style = { background: `${color}1f`, color }; // ~12% tint bg + full-strength text
  const inner = (
    <>
      <TaskTypeGlyph type="epic" color={color} className="h-3 w-3 shrink-0" />
      <span className="truncate">{label}</span>
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        title={`Epic: ${label}`}
        className={cn(cls, 'transition-opacity hover:opacity-80')}
        style={style}
      >
        {inner}
      </button>
    );
  }
  return (
    <span title={`Epic: ${label}`} className={cls} style={style}>
      {inner}
    </span>
  );
}
